import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Users, TrendingUp, CheckCircle, XCircle, Rocket, BarChart3, Building2, Calendar } from 'lucide-react';

const INVESTOR_FOCUS = ['Revenue quality', 'Efficiency', 'Predictability'];
const AI_CHANGES = ['Sales motions are changing', 'Product expectations are evolving', 'Efficiency is non-negotiable'];
const LEADER_TRAITS = ['Navigate ambiguity', 'Align product, GTM, and execution', 'Build for scale — with discipline'];

const APPROACH_STEPS = [
  { num: '01', title: 'GTM Understanding First', desc: 'ICP, ACV, sales cycle, inbound vs outbound mix.' },
  { num: '02', title: 'Role Definition Beyond JD', desc: 'Quota expectations, ramp timelines, pipeline ownership.' },
  { num: '03', title: 'Talent Mapping', desc: 'Identify candidates from relevant SaaS segments (PLG / Enterprise / Mid-market).' },
  { num: '04', title: 'Rigorous Screening', desc: 'Deal experience validation, quota and ARR metrics, sales process understanding.' },
  { num: '05', title: 'Cultural & Founder Fit', desc: 'Early-stage vs scale-up adaptability matched to your team.' },
];

const WHY_CHOOSE_REASONS = [
  'Access to pre-vetted SaaS sales talent pools',
  'Faster hiring cycles for revenue-critical roles',
  'Reduced hiring risk through performance-based screening',
  'Ability to build entire GTM teams, not just individuals',
];

const STAGE_CARDS = [
  { id: 'early', icon: Rocket, title: 'Early-stage SaaS startups', desc: '0 → $1M ARR' },
  { id: 'growth', icon: TrendingUp, title: 'Growth-stage companies', desc: '$1M → $10M ARR' },
  { id: 'scale', icon: Building2, title: 'Scale-ups building enterprise GTM', desc: 'Structured revenue motion' },
];

const CHALLENGES = [
  'Hiring reps who can\'t close',
  'SDRs who don\'t convert pipeline',
  'Sales leaders without SaaS DNA',
  'Mismatch between product & sales motion',
];

const REVENUE_LEADERSHIP = ['VP Sales / Head of Sales', 'Chief Revenue Officer (CRO)', 'Director – Enterprise Sales', 'Regional Sales Leaders'];
const MID_LEVEL = ['Account Executives (SMB / Mid-Market / Enterprise)', 'Sales Managers', 'Key Account Managers'];
const ENTRY_ROLES = ['SDRs / BDRs (Inbound & Outbound)', 'Sales Development Managers'];
const GROWTH_ROLES = ['Core Marketing (SEO/Email marketeer)', 'Product Marketing Manager', 'Sales & Data Operations'];

const BEST_FOR = [
  'Founders hiring their first sales team',
  'SaaS companies struggling with low conversion or missed quotas',
  'Startups scaling from founder-led sales to a structured GTM',
];

export const HeroSection = () => (
  <section className="hero-gradient pt-32 pb-20 px-4 sm:px-6 lg:px-8">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="leadership-hiring-title">
        Bespoke Leadership Hiring
      </h1>
      <p className="text-xl sm:text-2xl text-white/90 font-medium mb-4">SaaS Sales Hiring & Leadership Search</p>
      <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed mb-8">
        Build high-performance revenue teams for SaaS growth. From your first SDR to scaling enterprise sales teams — we help you identify, assess, and hire revenue talent that drives ARR from day one.
      </p>
      <Link to="/contact" data-testid="hiring-hero-cta"
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-8 py-4 hover:bg-accent/90 hover-lift transition-all group">
        30mins Consultation <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  </section>
);

export const IntroSection = () => (
  <section className="py-16 bg-background px-4 sm:px-6 lg:px-8">
    <div className="max-w-3xl mx-auto text-center">
      <p className="text-lg text-muted-foreground leading-relaxed">
        Hiring the right sales talent can define the trajectory of your SaaS company. Revenue hiring is not just about filling roles — it is about building predictable growth engines. We specialise in sales hiring for SaaS companies, helping you build teams that consistently deliver.
      </p>
    </div>
  </section>
);

export const WhyNowSection = ({ onScrollToConsultation }) => (
  <section className="py-20 lg:py-28 bg-primary/5 px-4 sm:px-6 lg:px-8" data-testid="why-now-section">
    <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground text-center mb-12">
        Why Avant-Garde <span className="text-accent">Now?</span>
      </h2>
      <div className="space-y-8">
        <div className="card-depth rounded-2xl p-8">
          <p className="text-muted-foreground leading-relaxed mb-4">
            Indian Start-ups have seen a lot of ups and downs in the last 10 years since the start around 2013-2014. First there were challenges for founders managing the India based captive with Sr. leadership and HQ in the US. While a healthy VC investment phase made several SaaS companies grow rapidly but at the expense of quality of resources, product roadmap with TAM fitment analysis, process improvements VS tool implementation and most importantly hiring the right leadership which had SaaS understanding.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-6">
            With the understanding of SaaS then came the time where they had to manage similar competitor companies that increased margins & investment due to product innovation. Post covid the market corrected.
          </p>
          <h3 className="text-xl font-bold text-foreground mb-4">Investors shifted focus from growth-at-all-costs to:</h3>
          <div className="flex flex-wrap gap-3 mb-4">
            {INVESTOR_FOCUS.map((item) => (
              <span key={item} className="px-5 py-2 rounded-full bg-accent/10 text-accent font-semibold text-sm border border-accent/15">{item}</span>
            ))}
          </div>
          <p className="text-muted-foreground leading-relaxed mb-8">Investors started to look for revenue as a main parameter for quality and bankability.</p>
          <div className="rounded-xl p-6 hero-gradient text-white mb-8">
            <h3 className="text-xl font-bold mb-4">The Next Inflection is AI & Reinvention</h3>
            <p className="text-white/80 leading-relaxed mb-6">We are now entering another defining phase. AI is not just a technology shift — it is a business model reset.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {AI_CHANGES.map((item) => (
                <div key={item} className="bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-center">
                  <span className="text-white/90 font-medium text-sm">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-white/80 leading-relaxed mb-4">The next generation of SaaS companies will be built by leaders who can:</p>
            <div className="flex flex-wrap gap-3">
              {LEADER_TRAITS.map((item) => (
                <span key={item} className="px-4 py-1.5 rounded-full bg-white/15 border border-white/25 text-white font-medium text-sm">{item}</span>
              ))}
            </div>
          </div>
          <p className="text-muted-foreground leading-relaxed mb-4">Working with over 4-5 SaaS founders as an employee and many other founders as an advisor and mentor, I have a deep understanding of what a company needs at what stage.</p>
          <blockquote className="border-l-4 border-accent pl-6 py-2">
            <p className="text-foreground font-semibold leading-relaxed">Hiring is not just following resumes and qualifying them but aligning to the present and the future of any organisation.</p>
          </blockquote>
        </div>
      </div>
      <div className="text-center mt-12">
        <button onClick={onScrollToConsultation} data-testid="consult-scroll-btn"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-8 py-4 hover:bg-accent/90 hover-lift transition-all group">
          Set up a free 30mins consultation <Calendar className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  </section>
);

const RoleCard = ({ icon: Icon, title, items, testId }) => (
  <div className="card-depth rounded-2xl p-7" data-testid={testId}>
    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-6">
      <Icon className="w-6 h-6 text-primary" />
    </div>
    <h3 className="text-xl font-bold text-foreground mb-5">{title}</h3>
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground">
          <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const WhatWeHireSection = () => (
  <section className="py-20 lg:py-28 bg-muted px-4 sm:px-6 lg:px-8" data-testid="what-we-hire">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground text-center mb-4">What We Help You Hire</h2>
      <p className="text-muted-foreground text-center mb-16 text-lg">Across the full revenue funnel — from pipeline generation to revenue leadership.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <RoleCard icon={Briefcase} title="Revenue Leadership" items={REVENUE_LEADERSHIP} testId="hire-revenue-leadership" />
        <RoleCard icon={Users} title="Mid-Level Sales Talent" items={MID_LEVEL} testId="hire-mid-level" />
        <RoleCard icon={TrendingUp} title="Entry & Pipeline Roles" items={ENTRY_ROLES} testId="hire-entry-pipeline" />
        <RoleCard icon={BarChart3} title="Key Growth Roles" items={GROWTH_ROLES} testId="hire-key-growth" />
      </div>
    </div>
  </section>
);

export const ApproachSection = () => (
  <section className="py-20 lg:py-28 bg-background px-4 sm:px-6 lg:px-8" data-testid="approach-section">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground text-center mb-4">Our Approach to SaaS Sales Hiring</h2>
      <p className="text-muted-foreground text-center mb-16 text-lg max-w-2xl mx-auto">
        SaaS sales hiring requires deep understanding of GTM motion, deal cycles, and buyer personas — not generic recruitment.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {APPROACH_STEPS.map((step) => (
          <div key={step.num} className="card-depth rounded-2xl p-8" data-testid={`approach-step-${step.num}`}>
            <span className="text-accent font-bold text-lg">{step.num}</span>
            <h3 className="text-xl font-bold text-foreground mt-3 mb-3">{step.title}</h3>
            <p className="text-muted-foreground leading-relaxed">{step.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const WhyChooseUsSection = () => (
  <section className="py-20 lg:py-28 bg-primary/5 px-4 sm:px-6 lg:px-8" data-testid="why-choose-us">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-4">Why SaaS Companies Choose Us</h2>
      <p className="text-muted-foreground text-center mb-12 text-lg">Data-driven hiring, leadership alignment, and fast turnaround for critical roles.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
        {WHY_CHOOSE_REASONS.map((item) => (
          <div key={item} className="flex items-start gap-3 bg-white rounded-xl p-5 shadow-sm border border-border">
            <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
            <span className="text-foreground font-medium">{item}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const SpecialisationSection = () => (
  <section className="py-20 lg:py-28 bg-background px-4 sm:px-6 lg:px-8" data-testid="specialisation-section">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-4">SaaS Sales Hiring Specialisation</h2>
      <p className="text-muted-foreground text-center mb-16 text-lg">Who we work with most often.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
        {STAGE_CARDS.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.id} className="card-depth rounded-2xl p-8 text-center" data-testid={`stage-card-${item.id}`}>
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-5">
                <Icon className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">{item.title}</h3>
              <p className="text-muted-foreground">{item.desc}</p>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-depth rounded-2xl p-8" data-testid="hiring-challenges">
          <h3 className="text-xl font-bold text-foreground mb-6">Hiring challenges we solve</h3>
          <ul className="space-y-4">
            {CHALLENGES.map((item) => (
              <li key={item} className="flex items-start gap-3 text-muted-foreground">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl p-8 bg-accent/5 border border-accent/15" data-testid="how-we-fix">
          <h3 className="text-xl font-bold text-foreground mb-4">How we fix it</h3>
          <p className="text-muted-foreground leading-relaxed">
            We align hiring with real GTM execution — assessing deal complexity, sales cycle ownership, buyer persona experience, and consistency of quota achievement.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export const BestForSection = () => (
  <section className="py-20 bg-background px-4 sm:px-6 lg:px-8" data-testid="best-for-section">
    <div className="max-w-3xl mx-auto text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-12">Who This Works Best For</h2>
      <div className="space-y-4">
        {BEST_FOR.map((item) => (
          <div key={item} className="flex items-center gap-3 bg-primary/5 rounded-xl px-6 py-4 text-left">
            <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="text-foreground font-medium">{item}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const CTASection = () => (
  <section className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="hiring-cta">
    <div className="absolute inset-0 cta-gradient animate-gradient" />
    <div className="absolute inset-0 hero-texture opacity-50" />
    <div className="relative z-10 max-w-3xl mx-auto text-center">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">Let's Build Your Revenue Engine</h2>
      <p className="text-lg text-white/75 mb-8 max-w-xl mx-auto">
        Whether you are hiring your first SDR or your VP of Sales, the goal is the same — build a team that drives predictable revenue growth.
      </p>
      <Link to="/contact" data-testid="hiring-cta-btn"
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-10 py-4 hover:bg-accent/90 hover-lift group">
        Contact us <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  </section>
);
